import axios from 'axios';

// Ensure this points to your FastAPI backend URL
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

// Default chunk size: 1MB (good for poor mobile connectivity)
const DEFAULT_CHUNK_SIZE = 1 * 1024 * 1024;

// Retry configuration
const MAX_RETRIES = 3;
const INITIAL_RETRY_DELAY = 1000; // 1 second

// Server-side processing is followed by polling, not by holding a request open.
const POLL_INTERVAL_MS = 3000;
const PROCESSING_TIMEOUT_MS = 30 * 60 * 1000;

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// --- Admin auth (HTTP Basic) ---
// The admin logs in once; the base64 token lives in localStorage and rides on
// every request. Read per-request (not at module load) so login/logout take
// effect immediately. Narrators never store a token and stay anonymous.
export const ADMIN_AUTH_KEY = 'pp_admin_auth';

apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem(ADMIN_AUTH_KEY);
  if (token) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Basic ${token}`;
  }
  return config;
});

// A 401 on a request that *carried* a token means the stored credentials are no
// longer valid (e.g. the admin password was rotated). Clear them and let the app
// drop back to the narrator view via the dispatched event.
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    const hadToken = Boolean(error.config?.headers?.Authorization);
    if (error.response?.status === 401 && hadToken) {
      localStorage.removeItem(ADMIN_AUTH_KEY);
      window.dispatchEvent(new Event('pp-admin-logout'));
    }
    return Promise.reject(error);
  }
);

/**
 * Report whether the currently-stored credentials are the admin's.
 * @returns {Promise<{is_admin: boolean}>}
 */
export const checkAdmin = () => apiClient.get('/auth/me').then((r) => r.data);

/**
 * Sleep for a specified duration
 * @param {number} ms - Milliseconds to sleep
 */
const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms));

/**
 * Execute a function with exponential backoff retry
 * @param {Function} fn - Async function to execute
 * @param {number} maxRetries - Maximum number of retries
 * @param {number} initialDelay - Initial delay in ms
 * @returns {Promise} - Result of the function
 */
const withRetry = async (fn, maxRetries = MAX_RETRIES, initialDelay = INITIAL_RETRY_DELAY) => {
  let lastError;
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      return await fn();
    } catch (error) {
      lastError = error;
      if (attempt < maxRetries) {
        const delay = initialDelay * Math.pow(2, attempt);
        console.log(`Retry attempt ${attempt + 1}/${maxRetries} after ${delay}ms`);
        await sleep(delay);
      }
    }
  }
  throw lastError;
};

// --- Video API Calls ---
export const getVideos = (params) => {
  // params: { skip, limit, sort_by, order, status }
  return apiClient.get('/videos/', { params });
};

export const getVideoById = (videoId, narrator = 'Won') => {
  return apiClient.get(`/videos/${videoId}`, { params: { narrator } });
};

// codePrefix: 'FCS' (auto-assigns FCS-<id>, `code` is ignored) or 'ECS'
// (requires a unique `code`); uniqueness is enforced server-side.
export const createVideo = ({ name, googleDriveLink, lengthSeconds, codePrefix, code }) => {
  return apiClient.post('/videos/', {
    name,
    google_drive_link: googleDriveLink,
    length_seconds: lengthSeconds,
    code_prefix: codePrefix,
    ...(codePrefix === 'ECS' ? { code } : {}),
  });
};

// Recording sessions + narration URLs used to *reenact* a narrator's take:
// replay the audio while auto-driving the video from the logged play/pause/seek.
export const getReenactData = (videoId, narrator = 'Won') => {
  return apiClient.get(`/videos/${videoId}/reenact`, { params: { narrator } });
};

// --- Session Management ---

// Every recording session for a video (both narrators, or one when given),
// active and deactivated alike — for the admin session management page.
export const getVideoSessions = (videoId, narrator = null) => {
  const params = narrator ? { narrator } : {};
  return apiClient.get(`/videos/${videoId}/sessions`, { params });
};

// Deactivate/reactivate a session (admin only; keyed by its start_record event
// id). A deactivated session is excluded from reenact as if deleted. The axios
// interceptor attaches the admin token.
export const setSessionDeactivated = (eventId, deactivated) => {
  return apiClient.patch(`/sessions/${eventId}`, { deactivated });
};

/** Create a synthetic session (start/stop + a success log) for manual audio.
 *  `replacesFile` marks audio that replaced the narrator's whole file rather than
 *  being appended — everything recorded before it is then superseded. */
export const createManualSession = ({ videoId, narrator, start, durationSeconds, replacesFile = false }) => {
  return apiClient.post('/sessions/', {
    video_id: videoId,
    narrator,
    start,
    duration_seconds: durationSeconds,
    replaces_file: replacesFile,
  });
};

/** Permanently delete a session. deletePlayback also wipes in-window play/pause/seek. */
export const deleteSession = (eventId, deletePlayback = false) => {
  return apiClient.delete(`/sessions/${eventId}`, {
    params: { delete_playback: deletePlayback },
  });
};

/** Diagnose why a session might be failing or omitted from reenact. */
export const diagnoseSession = (eventId) => {
  return apiClient.get(`/sessions/${eventId}/diagnose`);
};

/** Repair a corrupted or broken session according to its diagnosis. */
export const fixSession = (eventId) => {
  return apiClient.post(`/sessions/${eventId}/fix`);
};

/** Filtered admin log listing. */
export const queryLogs = (params = {}) => {
  return apiClient.get('/logs/', { params });
};

/** Bulk-insert play/pause/seek as offsets from a session start. */
export const bulkCreateLogs = ({ startEventId, events, reprocess = true }) => {
  return apiClient.post('/logs/bulk', {
    start_event_id: startEventId,
    events,
    reprocess,
  });
};

/** Hard-delete a single log row. */
export const deleteLogEvent = (logId) => {
  return apiClient.delete(`/logs/${logId}`);
};

// --- Chunked Upload API Calls ---

/**
 * Split a Blob into chunks
 * @param {Blob} blob - The blob to split
 * @param {number} chunkSize - Size of each chunk in bytes
 * @returns {Blob[]} - Array of chunk blobs
 */
const splitBlobIntoChunks = (blob, chunkSize = DEFAULT_CHUNK_SIZE) => {
  const chunks = [];
  let offset = 0;

  while (offset < blob.size) {
    const end = Math.min(offset + chunkSize, blob.size);
    chunks.push(blob.slice(offset, end));
    offset = end;
  }

  return chunks;
};

/**
 * Re-cut a recording into the exact slices it was uploaded in while recording.
 * Lets a retry resume a streamed session instead of re-sending the whole file.
 * @param {Blob} blob - The complete recording
 * @param {number[]} sliceSizes - Byte length of each slice, in order
 * @returns {Blob[]|null} - The slices, or null if they don't add up to the blob
 */
export const sliceBlobBySizes = (blob, sliceSizes) => {
  if (!Array.isArray(sliceSizes) || sliceSizes.length === 0) return null;
  const total = sliceSizes.reduce((sum, size) => sum + size, 0);
  if (total !== blob.size) return null;

  const slices = [];
  let offset = 0;
  for (const size of sliceSizes) {
    slices.push(blob.slice(offset, offset + size));
    offset += size;
  }
  return slices;
};

/**
 * Initialize a chunked upload session
 * @param {number} videoId - Video ID
 * @param {string} narrator - Narrator name
 * @param {string} filename - Filename
 * @param {number} totalChunks - Total number of chunks
 * @returns {Promise<Object>} - Upload session info including upload_id
 */
export const initChunkedUpload = async (videoId, narrator, filename, totalChunks) => {
  const response = await apiClient.post('/uploads/init', {
    video_id: videoId,
    narrator: narrator,
    filename: filename,
    total_chunks: totalChunks,
  });
  return response.data;
};

/**
 * Get the status of a chunked upload session
 * @param {string} uploadId - Upload session ID
 * @returns {Promise<Object>} - Upload status info
 */
export const getUploadStatus = async (uploadId) => {
  const response = await apiClient.get(`/uploads/${uploadId}`);
  return response.data;
};

/**
 * Upload a single chunk with retry logic
 * @param {string} uploadId - Upload session ID
 * @param {number} chunkIndex - Index of this chunk
 * @param {Blob} chunkBlob - The chunk data
 * @returns {Promise<Object>} - Chunk upload response
 */
export const uploadChunk = async (uploadId, chunkIndex, chunkBlob) => {
  return withRetry(async () => {
    const formData = new FormData();
    formData.append('chunk_file', chunkBlob, `chunk_${chunkIndex}`);
    
    const response = await apiClient.post(`/uploads/${uploadId}/chunk`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      params: {
        chunk_index: chunkIndex,
      },
      timeout: 30000, // 30 second timeout per chunk
    });
    
    return response.data;
  });
};

/**
 * Ask the server to assemble and publish an upload. Returns as soon as the work
 * is queued - follow it with pollUploadStatus.
 * @param {string} uploadId - Upload session ID
 * @param {Object} options
 * @param {number} options.totalChunks - Final chunk count (for a session opened while recording)
 * @param {boolean} options.force - Process even if the audio may already have been added
 * @returns {Promise<Object>} - The upload session, now processing
 */
const completeChunkedUpload = async (uploadId, { totalChunks, force } = {}) => {
  const body = {};
  if (totalChunks != null) body.total_chunks = totalChunks;
  if (force) body.force = true;

  const response = await apiClient.post(`/uploads/${uploadId}/complete`, body);
  return response.data;
};

/**
 * Wait for the server to finish processing an upload.
 *
 * Processing runs on the server, so a dropped connection here costs nothing -
 * network errors keep the poll alive and only a server-reported failure rejects.
 *
 * @param {string} uploadId - Upload session ID
 * @param {Object} options
 * @param {Function} options.onProgress - Progress callback (90-100)
 * @param {number} options.intervalMs - How often to check
 * @param {number} options.timeoutMs - How long to keep checking
 * @param {Function} options.shouldStop - Return true to stop watching (resolves to null)
 * @returns {Promise<Object|null>} - The completed upload session, or null if stopped
 */
export const pollUploadStatus = async (uploadId, { onProgress, intervalMs = POLL_INTERVAL_MS, timeoutMs = PROCESSING_TIMEOUT_MS, shouldStop } = {}) => {
  const deadline = Date.now() + timeoutMs;

  while (Date.now() < deadline) {
    await sleep(intervalMs);
    if (shouldStop && shouldStop()) return null;

    let session;
    try {
      session = await getUploadStatus(uploadId);
    } catch (error) {
      // The recording is safe on the server; keep waiting for the network.
      console.warn('Could not read upload status, retrying:', error.message);
      continue;
    }

    if (session.status === 'complete') {
      if (onProgress) onProgress(100);
      return session;
    }
    if (session.status === 'failed') {
      throw new Error(session.error_message || 'Processing failed');
    }
    if (onProgress) onProgress(95);
  }

  throw new Error('การประมวลผลใช้เวลานานเกินไป กรุณาลองใหม่อีกครั้ง');
};

/**
 * Process (or re-process) a recording the server already holds every chunk of.
 * @param {string} uploadId - Upload session ID
 * @param {Object} options
 * @param {boolean} options.force - Process even if the audio may already have been added
 * @param {Function} options.onProgress - Progress callback
 * @returns {Promise<Object>} - The completed upload session
 */
export const retryProcessing = async (uploadId, { force, onProgress, ...pollOptions } = {}) => {
  await completeChunkedUpload(uploadId, { force });
  if (onProgress) onProgress(95);
  return pollUploadStatus(uploadId, { onProgress, ...pollOptions });
};

/**
 * Upload sessions this video still holds a recording for, for the given narrator.
 * These are recoverable from any device, even one that never made the recording.
 * @param {number} videoId - Video ID
 * @param {string} narrator - Narrator name
 * @returns {Promise<Array>} - Recoverable upload sessions
 */
export const getVideoUploads = async (videoId, narrator) => {
  const response = await apiClient.get(`/videos/${videoId}/uploads`, { params: { narrator } });
  return response.data;
};

/**
 * Send every chunk the server is still missing.
 * @param {string} uploadId - Upload session ID
 * @param {Blob[]} chunks - All chunks of the recording, in order
 * @param {number[]} receivedChunks - Indices the server already has
 * @param {Object} options
 * @param {Function} options.onProgress - Progress callback (0-90)
 * @param {Function} options.onChunkComplete - Called after each chunk lands
 */
const uploadMissingChunks = async (uploadId, chunks, receivedChunks = [], { onProgress, onChunkComplete } = {}) => {
  const totalChunks = chunks.length;
  let completedChunks = receivedChunks;

  for (let i = 0; i < totalChunks; i++) {
    if (completedChunks.includes(i)) continue;

    try {
      const response = await uploadChunk(uploadId, i, chunks[i]);
      completedChunks = response.received_chunks;
    } catch (error) {
      console.error(`Failed to upload chunk ${i} after retries:`, error);
      throw new Error(`อัพโหลดไม่สำเร็จที่ส่วน ${i + 1}/${totalChunks} สามารถลองอัพโหลดต่อได้`);
    }

    // Reserve the last 10% for server-side processing.
    if (onProgress) onProgress(Math.round((completedChunks.length / totalChunks) * 90));
    if (onChunkComplete) onChunkComplete({ uploadId, chunkIndex: i, completedChunks, totalChunks });
  }

  return completedChunks;
};

/**
 * Cancel and cleanup a chunked upload
 * @param {string} uploadId - Upload session ID
 */
export const cancelChunkedUpload = async (uploadId) => {
  await apiClient.delete(`/uploads/${uploadId}`);
};

/**
 * Perform a complete chunked upload with progress tracking
 * @param {number} videoId - Video ID
 * @param {string} narrator - Narrator name
 * @param {Blob} audioBlob - The complete audio blob
 * @param {string} filename - Filename
 * @param {Object} options - Options
 * @param {Function} options.onProgress - Progress callback (0-100)
 * @param {Function} options.onChunkComplete - Called after each chunk completes
 * @param {string} options.resumeUploadId - Upload ID to resume (optional)
 * @returns {Promise<Object>} - Audio description response
 */
export const chunkedUpload = async (videoId, narrator, audioBlob, filename, options = {}) => {
  const { onProgress, onChunkComplete, resumeUploadId, sliceSizes, ...pollOptions } = options;

  // Prefer the slices the recording was streamed in, so a retry only sends what
  // is missing; otherwise cut the finished file into fixed-size chunks.
  const chunks = sliceBlobBySizes(audioBlob, sliceSizes) || splitBlobIntoChunks(audioBlob);
  const totalChunks = chunks.length;

  console.log(`Starting chunked upload: ${totalChunks} chunks, total size: ${audioBlob.size} bytes`);

  let uploadId = resumeUploadId;
  let completedChunks = [];

  if (uploadId) {
    try {
      const session = await getUploadStatus(uploadId);

      if (session.status === 'complete') {
        if (onProgress) onProgress(100);
        return session;
      }
      if (session.status === 'processing') {
        if (onProgress) onProgress(95);
        return await pollUploadStatus(uploadId, { onProgress, ...pollOptions });
      }

      completedChunks = session.received_chunks || [];

      // A narrator can only finalize a PENDING session (a fresh completion);
      // retrying a FAILED one is an admin-only action, so it must never be
      // resumed-and-completed here. A session cut into different chunks than we
      // hold can't be resumed either. In both cases discard and start over — the
      // new session goes through stage-then-swap plus the server's duplicate-risk
      // check, so it can never double-append this recording.
      const isResumable =
        session.status === 'pending' &&
        (session.total_chunks === totalChunks || session.total_chunks === -1);
      const hasEveryChunk = completedChunks.length === totalChunks;

      if (session.status !== 'pending' || (!isResumable && !hasEveryChunk)) {
        console.warn(`Upload ${uploadId} cannot be resumed (${session.status}, ${session.total_chunks} chunks); starting fresh`);
        await cancelChunkedUpload(uploadId).catch((e) => console.warn('Could not discard stale session:', e.message));
        uploadId = null;
        completedChunks = [];
      } else {
        console.log(`Resuming upload ${uploadId}, ${completedChunks.length}/${totalChunks} chunks already uploaded`);
      }
    } catch (error) {
      console.warn('Could not resume upload, starting fresh:', error);
      uploadId = null;
      completedChunks = [];
    }
  }

  if (!uploadId) {
    const initResponse = await initChunkedUpload(videoId, narrator, filename, totalChunks);
    uploadId = initResponse.upload_id;
    console.log(`Initialized new upload: ${uploadId}`);
  }

  await uploadMissingChunks(uploadId, chunks, completedChunks, { onProgress, onChunkComplete });

  console.log('All chunks uploaded, processing on the server...');
  if (onProgress) onProgress(95);

  // From here the recording is safe on the server: processing runs there and
  // survives anything that happens to this browser.
  await completeChunkedUpload(uploadId, { totalChunks });
  return pollUploadStatus(uploadId, { onProgress, ...pollOptions });
};

// --- money API Calls ---
export const getPendingPaidAmount = () => {
  return apiClient.get('/pending_paid_amount');
}

// Mark all reviewed items as paid (for narrator "Won" or "Waan")
export const paidAllReviewed = (narrator = 'Won') => {
  return apiClient.post('/audio_descriptions/paid_all', null, { params: { narrator } });
}


// --- Log API Calls ---
export const logEvent = (logData) => {
  // logData: { video_id, action, value, narrator }
  // convert value to string if it's a number
  if (typeof logData.value === 'number') {
    logData.value = logData.value.toString();
  }
  // add timestamp of current utc time in iso format ignore local time
  logData.timestamp = new Date().toISOString();
  return apiClient.post('/logs/', logData);
};

// Insert a log row with an explicit timestamp (the admin log page's hand-insert
// form). Unlike logEvent, the caller's timestamp survives — it must already be
// UTC ISO ("…Z"), because SQLite's DateTime binding drops tzinfo *without*
// converting, so a "+07:00" value would be stored as though it were UTC.
export const createLogEvent = (logData) => {
  return apiClient.post('/logs/', logData);
};

// --- Optional: Update reviewed Status ---
export const updateReviewedStatus = (recordingId, narrator, isReviewed) => {
    return apiClient.patch(`/recordings/${recordingId}/status`, { is_reviewed: isReviewed },
    {
      params: {
        narrator: narrator,
      },
    });
}

// --- Update paid Status ---
export const updatePaidStatus = (recordingId, narrator, isPaid) => {
    return apiClient.patch(`/recordings/${recordingId}/paid`, { is_paid: isPaid },
    {
      params: {
        narrator: narrator,
      },
    });
}

// --- Update "needs more" revert flag ---
// needsMore=true sends a RECORDED take back to the NEW list (keeping its audio);
// false flips it back to RECORDED. `note` (what's missing) is written when a
// string is passed; pass null/undefined to leave the note unchanged.
export const updateNeedsMore = (recordingId, narrator, needsMore, note) => {
    const body = { needs_more: needsMore };
    if (note !== undefined && note !== null) body.note = note;
    return apiClient.patch(`/recordings/${recordingId}/needs-more`, body,
    {
      params: {
        narrator: narrator,
      },
    });
}

// --- Manual Upload Audio ---
export const manualUploadAudio = (videoId, narrator, audioFile, onProgress) => {
    const formData = new FormData();
    formData.append('audio_file', audioFile);
    
    return apiClient.post(`/videos/${videoId}/manual-upload`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      params: {
        narrator: narrator,
      },
      onUploadProgress: (progressEvent) => {
        if (onProgress && progressEvent.total) {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          onProgress(percentCompleted);
        }
      },
    });
}

// --- Update Video Metadata ---
export const updateVideoMetadata = (videoId, metadata) => {
    // metadata: { name?, cost_thb?, cost_thb_waan? }
    return apiClient.patch(`/videos/${videoId}/metadata`, metadata);
}

// --- Reprocess Audio ---
export const reprocessAudio = (videoId, narrator, startTimeISO, sessionAction = 'auto') => {
    return apiClient.post(`/videos/${videoId}/reprocess`, {
      narrator: narrator,
      start_time_iso: startTimeISO,
      session_action: sessionAction,
    }, {
      timeout: 120000, // 2 minute timeout for processing
    });
}

// --- App Settings ---
export const getSettings = () => {
    return apiClient.get('/settings');
};

export const updateSettings = (settings) => {
    // settings: { preload_video?: boolean }
    return apiClient.patch('/settings', settings);
};

// --- Hide/Show Video ---
export const hideVideo = (videoId) => {
    return apiClient.patch(`/videos/${videoId}/hide`);
};

export const showVideo = (videoId) => {
    return apiClient.patch(`/videos/${videoId}/show`);
};
